import React from 'react';
import PropTypes from 'prop-types';
import { FiChevronRight } from 'react-icons/fi';
import { useLocation, useRouteMatch } from 'react-router';
import { Link } from 'react-router-dom';

import { __t } from '@mssgme/shared';
import { ua, parseUrl, generateDeepLink, resolvePathname } from '@mssgme/helpers';
import { CardMessengers, MessengerButton } from '@mssgme/cards';
import PageProductGallery from '../PageProductGallery';

import styles from './styles.scss';

const addUrl = (product, card) => {
    const text = encodeURIComponent(
        `${__t('buy_template')} ${product.name} - ${card.profile.absoluteUrl}/p/${product.slug}`
    );
    const ref = `buy_${product._id}_${Date.now()}_${document.documentElement.lang}`;

    return (messenger) => {
        let openDialog = true;

        const link = generateDeepLink(messenger);

        if (link.isSupported('text')) {
            link.addParam('text', text);
            openDialog = false;
        } else if (link.isSupported('ref')) {
            link.addParam('ref', ref);
            openDialog = false;
        } else if (link.isSupported('start')) {
            link.addParam('start', ref);
            openDialog = false;
        }

        const url = ua.isMobile ? link.getUrl() : link.getRedirectUrl();

        return { ...messenger, url, openDialog };
    };
};

export default function PageProducts({ card, product, unmount, lastCollectionSlug }) {
    const match = useRouteMatch();
    const location = useLocation();

    const cardMessengers = card.messengers.map(addUrl(product, card));
    const hasExternalUrl = product.externalUrl || (product.externalButton && product.externalButton.url);
    const externalUrlObj =
        (product.externalButton && product.externalButton.url && parseUrl(product.externalButton.url)) ||
        (product.externalUrl && parseUrl(product.externalUrl)) ||
        {};
    const defaultCollection = card.collections.items.find((col) => col.default);
    const fromCollection = (location.state && location.state.fromCollection) || defaultCollection._id;

    const prevProductLink = product.gauge.prev
        ? { pathname: resolvePathname(match.url, '..', product.gauge.prev.slug), state: { fromCollection } }
        : null;
    const nextProductLink = product.gauge.next
        ? { pathname: resolvePathname(match.url, '..', product.gauge.next.slug), state: { fromCollection } }
        : null;
    const backUrl = resolvePathname(match.url, lastCollectionSlug ? `../../c/${lastCollectionSlug}` : '../..');
    const storeUrl = resolvePathname(match.url, '../..');

    return (
        <div className={styles.root}>
            <div className={styles.inner}>
                <PageProductGallery
                    key={product.slug}
                    backUrl={backUrl}
                    photos={product.photos}
                    nextProductLink={nextProductLink}
                    prevProductLink={prevProductLink}
                    unmount={unmount}
                />
                <div className={styles.productInfo}>
                    <div className={styles.content}>
                        <div className={styles.productInfoHeaderPane}>
                            <div className={styles.productInfoHeaderPaneMeta}>
                                <div className={styles.productInfoHeaderPaneMeta}>
                                    <h1>{product.name}</h1>
                                    {product.price && (
                                        <div className={styles.priceContainer}>
                                            {product.price.formattedDiscount && product.price.formattedDiscount}
                                            {product.price.amount === 0 ? (
                                                __t('free')
                                            ) : (
                                                <span
                                                    className={product.price.formattedDiscount ? styles.oldPrice : null}
                                                >
                                                    {product.price.formatted}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            {cardMessengers.length > 0 && (
                                <CardMessengers placement="product">
                                    {cardMessengers.map((m) => (
                                        <MessengerButton
                                            key={m.messenger}
                                            href={m.url}
                                            platform={m.messenger}
                                            data-product-messenger={m.messenger}
                                        />
                                    ))}
                                </CardMessengers>
                            )}
                        </div>
                        {hasExternalUrl && (
                            <a
                                data-id="product_website_link"
                                href={(product.externalButton && product.externalButton.url) || product.externalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.websiteLinkContainer}
                            >
                                <span className={styles.linkName}>
                                    {product.externalButton && product.externalButton.title
                                        ? product.externalButton.title
                                        : __t('visit_website')}
                                </span>
                                <span className={styles.linkDomain}>{externalUrlObj.hostname}</span>
                            </a>
                        )}
                        {product.description && (
                            <div className={styles.productInfoDescription}>
                                <h2>{__t('product_details')}</h2>
                                <p>{product.description}</p>
                            </div>
                        )}
                    </div>
                    <Link onClick={(e) => unmount(e, storeUrl, true)} to={storeUrl} className={styles.storePane}>
                        <div className={styles.storePaneContent}>
                            <div className={styles.storePaneMeta}>
                                <div className={styles.storePaneAvatar}>
                                    <img src={card.design.photoUrl} alt={card.profile.fullname} />
                                </div>
                                <div className={styles.storePaneInfo}>
                                    <h3>{card.profile.fullname}</h3>
                                    {card.profile.category && card.profile.category !== 'category_not_business' && (
                                        <span>{__t(card.profile.category)}</span>
                                    )}
                                </div>
                            </div>
                            <div className={styles.storePaneIcon}>
                                <FiChevronRight size={20} />
                            </div>
                        </div>
                    </Link>
                </div>
            </div>
        </div>
    );
}

PageProducts.propTypes = {
    card: PropTypes.object.isRequired,
    product: PropTypes.object.isRequired,
    unmount: PropTypes.func.isRequired,
    isFetching: PropTypes.bool.isRequired,
    lastCollectionSlug: PropTypes.any.isRequired,
};
